import { Box, Paper, IconButton, Tooltip } from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

import { useEffect, useState } from "react";
import { getLoans } from "../../api/api";
import { useNavigate } from "react-router-dom";

import DataTable from "../../components/datatable/DataTable";
import StatusChip from "../../components/datatable/StatusChip";
import FilterBar from "../../components/common/FilterBar";

import { GridColDef } from "@mui/x-data-grid";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

const Loans = () => {
  const [loans, setLoans] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 🔥 FETCH
  const fetchLoans = async () => {
    try {
      setLoading(true);

      // 🔥 DEFAULT STATUS
      let finalStatus = "approved,disbursed";

      // 🔥 FILTER OVERRIDE
      if (status) {
        finalStatus = status;
      }

      const params: any = {
        status: finalStatus,
      };

      if (search) {
        params.search = search;
      }

      const res = await getLoans(params);

      setLoans(res.data?.data || []);
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to fetch loans",

          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(fetchLoans, 400);
    return () => clearTimeout(delay);
  }, [search, status]);

  // 🔥 COLUMNS (Leads जैसा)
  const columns: GridColDef[] = [
    {
      field: "leadCode",
      headerName: "Lead ID",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return row?.leadId?.leadCode || "-";
      },
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return row?.leadId?.customerName || "-";
      },
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return row?.leadId?.mobile || "-";
      },
    },
    {
      field: "productType",
      headerName: "Product",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return row?.leadId?.productType || "-";
      },
    },
    {
      field: "loanAmount",
      headerName: "Amount",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return row?.loanAmount
          ? `₹ ${Number(row.loanAmount).toLocaleString("en-IN")}`
          : "-";
      },
    },
    {
      field: "netAmount",
      headerName: "Net Amount",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;

        // 🔥 PRIORITY LOGIC
        const net =
          row?.disbursement?.netDisbursement || // disbursement done
          row?.netAmount || // credit stage
          0;

        return net ? `₹ ${Number(net).toLocaleString("en-IN")}` : "-";
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <StatusChip status={params.row?.leadId?.status} />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 1,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "-",
    },

    // 🔥 ACTIONS (same style)
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* VIEW */}
            <Tooltip title="View Loan">
              <IconButton
                size="small"
                onClick={() => navigate(`/loans/view/${row._id}`)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* DISBURSEMENT */}
            <Tooltip title="Disbursement">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(
                    row?.leadId?.status === "approved"
                      ? `/disbursement/create/${row._id}`
                      : `/disbursement/view/${row._id}`,
                  )
                }
                sx={{
                  background: "#dcfce7",
                  color: "#16a34a",
                }}
              >
                <AccountBalanceIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 1 }}>
      <FilterBar
        title="Loan Management"
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
      />

      <Paper
        sx={{
          mt: 2,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <DataTable rows={loans} columns={columns} loading={loading} />
      </Paper>
    </Box>
  );
};

export default Loans;
